import { handleWidgetRenderRequest } from '@/components/dashboard/widgetRenderProxy';
import { api } from '@/lib/api';
import { type RefObject, useEffect } from 'react';

interface BridgeOptions {
  onQueryExpired?: (queryId: string | null) => void;
}

/**
 * dashboard view 페이지용 iframe message bridge.
 * useArtifactBridge 와 같은 RPC 프로토콜이지만, /api/dashboards/:id/rpc 로 라우팅 — owner 가
 * 아닌 viewer 도 호출 가능 (dashboard 공유 권한이 grant 역할). runSavedQuery 만 지원.
 */
export function useDashboardBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  dashboardId: string,
  options: BridgeOptions = {},
): void {
  const { onQueryExpired } = options;
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || ev.source !== iframe.contentWindow) return;
      const d = ev.data;
      if (!d || typeof d !== 'object') return;

      // widget-render 매개 (duplicate / change_kind 시 inline JS 가 요청).
      const kind = (d as { kind?: unknown }).kind;
      if (kind === 'dashboard.widget-render-request') {
        handleWidgetRenderRequest(iframe, {
          reqId: (d as { reqId?: unknown }).reqId,
          payload: (d as { payload?: unknown }).payload,
        });
        return;
      }

      // 클라이언트 SPEC mutation 결과 영속화 — owner 가 위젯 ⋮ 메뉴로 편집했을 때.
      // fire-and-forget: await 안 함. 실패해도 UI 는 이미 반영된 상태 유지.
      if (kind === 'dashboard.spec.changed') {
        const spec = (d as { spec?: unknown }).spec;
        if (spec && typeof spec === 'object') {
          void api
            .dashboardSetSpec(dashboardId, {
              spec: spec as Parameters<typeof api.dashboardSetSpec>[1]['spec'],
            })
            .catch((err) => {
              console.warn(
                '[dashboard] spec 영속화 실패 (UI 는 반영됨, 새로고침 시 이전 상태로 복귀):',
                err,
              );
            });
        }
        return;
      }

      const reqId = (d as { reqId?: unknown }).reqId;
      const methodRaw = (d as { method?: unknown }).method;
      const params = (d as { params?: unknown }).params;
      if (typeof reqId !== 'number') return;

      const replyOk = (data: unknown) => {
        iframe.contentWindow?.postMessage({ reqId, ok: true, data }, '*');
      };
      const replyErr = (error: string) => {
        iframe.contentWindow?.postMessage({ reqId, ok: false, error }, '*');
      };

      if (methodRaw !== 'runSavedQuery') {
        replyErr(`대시보드 view 는 ${String(methodRaw)} 를 지원하지 않습니다.`);
        return;
      }

      void (async () => {
        try {
          const res = await api.dashboardRpc(dashboardId, 'runSavedQuery', params);
          if (res.ok) {
            replyOk(res.data);
          } else {
            if (res.code === 'query_expired' && onQueryExpired) {
              const qid =
                params && typeof params === 'object' && params !== null
                  ? (params as { queryId?: unknown }).queryId
                  : undefined;
              onQueryExpired(typeof qid === 'string' ? qid : null);
            }
            replyErr(res.error);
          }
        } catch (err) {
          replyErr(err instanceof Error ? err.message : 'RPC 호출 실패');
        }
      })();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [iframeRef, dashboardId, onQueryExpired]);
}
